import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { kpiApi } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';

/**
 * The KPIs every employee is measured on, whatever their role.
 *
 * Three of these have always existed — late coming, suggestions, kaizen — but
 * they were a list in the source code, so "everyone should also be measured on
 * safety" was a release rather than a decision. This is that decision, made
 * here.
 *
 * Two things about them are easy to get wrong and are stated on screen rather
 * than left to be discovered:
 *
 *   WEIGHT 0. These rows are recorded, not weighted. The monthly percentage
 *   divides by the KPIs that actually carry a mark, so adding one here does
 *   not dilute anybody's score — which is the first thing anyone fears.
 *
 *   REMOVING IS NOT RETROACTIVE. A row that already carries a recorded mark
 *   stays on that sheet, because pulling a scored row out of a settled month
 *   silently rewrites that month's percentage.
 */

export default function MandatoryKpis({ masterFlat = [], onChanged }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [adding, setAdding] = useState(false);
  const [q, setQ] = useState('');

  const load = useCallback(async () => {
    setBusy(true); setErr(null);
    try {
      const r = await kpiApi.mandatoryKpis();
      setData(r.data);
    } catch (e) {
      setErr({ status: e?.response?.status,
               detail: e?.response?.data?.detail || 'Could not read the list' });
    } finally { setBusy(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const current = useMemo(() => (data?.kpis || []), [data]);
  const currentKeys = useMemo(
    () => current.map((k) => k.kpi_key).filter(Boolean), [current]);

  // Only KPIs the master actually holds, and never one already on the list.
  const choices = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (masterFlat || [])
      .filter((m) => m.active !== false && !currentKeys.includes(m.key))
      .filter((m) => !needle || (m.name || '').toLowerCase().includes(needle)
                             || (m.key || '').toLowerCase().includes(needle))
      .slice(0, 40);
  }, [masterFlat, currentKeys, q]);

  const save = async (keys, what) => {
    setBusy(true);
    try {
      const r = await kpiApi.saveMandatoryKpis(keys);
      const d = r.data || {};
      const a = d.applied || {};
      toast.success(`${what} — ${a.templates_updated || 0} sheet template(s) and `
        + `${a.sheets_updated || 0} employee sheet(s) updated`);
      // Everything the press could not do, said plainly rather than left to be
      // noticed later on one person's sheet.
      (d.removal_detail || []).forEach((x) => {
        if (x.sheets_kept_because_scored) {
          toast(`${x.kpi}: kept on ${x.sheets_kept_because_scored} sheet(s) that already `
                + 'carry a mark — removing it would change a settled month', { icon: 'ℹ️' });
        }
        if (x.locked_skipped) {
          toast(`${x.kpi}: ${x.locked_skipped} locked sheet(s) untouched`, { icon: '🔒' });
        }
      });
      if (a.locked_skipped) {
        toast(`${a.locked_skipped} locked sheet(s) could not receive the new row — `
              + 'they need unlocking', { icon: '🔒' });
      }
      setAdding(false); setQ('');
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save the list', { duration: 9000 });
    } finally { setBusy(false); }
  };

  const add = (m) => save([...currentKeys, m.key], `“${m.name}” now applies to everyone`);

  const remove = (row) => {
    if (!row.kpi_key) {
      toast.error('This row predates the editable list and has no KPI key, so it '
                  + 'cannot be removed from here.');
      return;
    }
    if (!window.confirm(
      `Stop measuring everyone on “${row.kpi}”?\n\n`
      + `It comes off ${row.on_templates} sheet template(s) and off every employee `
      + 'sheet where it has NO recorded mark.\n\n'
      + 'Rows that already carry a mark stay, so no settled month changes. '
      + 'Locked sheets are not touched.')) return;
    save(currentKeys.filter((k) => k !== row.kpi_key), `“${row.kpi}” removed from everyone`);
  };

  if (err) {
    return (
      <div className="rounded-xl border border-[#FBC9D6] bg-[#FFF5F7] px-4 py-3"
        data-testid="mandatory-error">
        <p className="text-sm font-medium text-[#B91C1C]">
          {err.status === 403 ? 'You do not have permission to see this'
                              : 'The list could not be read'}
        </p>
        <p className="text-[12px] text-[#7A4A12] mt-0.5">{err.detail}</p>
      </div>
    );
  }

  const t = data?.totals || {};
  return (
    <div data-testid="mandatory-kpis">
      <div className="flex items-baseline gap-2 mb-1 flex-wrap">
        <h3 className="font-medium text-[#11182B]">Everyone&rsquo;s KPIs</h3>
        <p className="text-sm text-[#5A6377]">
          Measured on every employee, whatever their role.
        </p>
        <span className="ml-auto text-[11px] text-[#98A1B2]">
          {t.active_employees ?? '—'} active employees · {t.templates ?? '—'} sheet templates
        </span>
      </div>
      <p className="text-[12px] text-[#5A6377] mb-3 max-w-3xl">
        A KPI added here is appended to every sheet template and every active
        employee&rsquo;s sheet, <strong>at weight 0</strong>. These rows are recorded, not
        weighted — the monthly percentage divides by the KPIs that carry a mark, so
        adding one does not dilute anybody&rsquo;s score.
      </p>

      {busy && !data && (
        <p className="text-sm text-[#98A1B2]" data-testid="mandatory-loading">Reading…</p>
      )}

      {!!current.length && (
        <div className="rounded-xl border border-[#E6E9F0] bg-white overflow-hidden mb-3">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#F8FAFC] text-[#98A1B2] text-[11px] uppercase">
                <th className="text-left font-semibold px-3 py-2">KPI</th>
                <th className="text-left font-semibold px-3 py-2 w-44">Reached</th>
                <th className="px-3 py-2 w-24"></th>
              </tr>
            </thead>
            <tbody>
              {current.map((k) => {
                const short = k.on_sheets < k.of_sheets;
                return (
                  <tr key={k.kpi} className="border-t border-[#F2F4F8] align-top"
                    data-testid={`mandatory-row-${k.kpi_key || k.kpi}`}>
                    <td className="px-3 py-2">
                      <div className="text-[#11182B]">{k.kpi}</div>
                      {k.kpi_key && (
                        <div className="font-mono text-[10.5px] text-[#B4B9CC]">{k.kpi_key}</div>
                      )}
                      <div className="flex gap-2 flex-wrap mt-0.5">
                        {/* A row nobody can score is worth saying out loud: it
                            lands on 90 sheets and then withholds every mark. */}
                        {k.in_master && !k.has_ladder && (
                          <span className="text-[10px] text-[#B14A0E]">no marking ladder yet</span>
                        )}
                        {!k.in_master && (
                          <span className="text-[10px] text-[#B91C1C]">not in KPI Master</span>
                        )}
                        {k.master_retired && (
                          <span className="text-[10px] text-[#B91C1C]">retired in KPI Master</span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-[12px] text-[#5A6377]">
                      <div>{k.on_templates} of {k.of_templates} templates</div>
                      <div className={short ? 'text-[#B14A0E]' : ''}>
                        {k.on_sheets} of {k.of_sheets} employee sheets
                      </div>
                      {/* The gap is nearly always a signed-off sheet, and that
                          is a different problem from a failed update. */}
                      {!!k.locked_missing && (
                        <div className="text-[11px] text-[#98A1B2]">
                          {k.locked_missing} locked sheet(s) need unlocking
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button onClick={() => remove(k)} disabled={busy}
                        data-testid={`mandatory-remove-${k.kpi_key || k.kpi}`}
                        className="text-[11px] text-[#C2151F] hover:underline disabled:opacity-50">
                        remove
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {data && !current.length && (
        <p className="text-sm text-[#98A1B2] py-4" data-testid="mandatory-empty">
          Nothing applies to everyone yet. Add a KPI below and it is appended to every
          sheet.
        </p>
      )}

      {adding ? (
        <div className="rounded-xl border border-[#C7D7FE] bg-[#F5F8FF] p-3">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <span className="text-[11px] uppercase tracking-wide text-[#8B8FA8]">
              Pick a KPI from the master
            </span>
            <button onClick={() => { setAdding(false); setQ(''); }}
              className="ml-auto text-[11px] text-[#98A1B2] hover:text-[#11182B]">cancel</button>
          </div>
          <Input value={q} onChange={(e) => setQ(e.target.value)} autoFocus
            placeholder="Search — safety, saving, suggestion…"
            data-testid="mandatory-search" className="max-w-md text-sm mb-2" />
          <div className="max-h-64 overflow-y-auto rounded-lg border border-[#E6E9F0] bg-white">
            {choices.length ? choices.map((m) => (
              <button key={m.key} onClick={() => add(m)} disabled={busy}
                data-testid={`mandatory-add-${m.key}`}
                className="w-full text-left px-3 py-2 border-b border-[#F2F4F8] last:border-0
                           hover:bg-[#F5F8FF] disabled:opacity-50">
                <span className="block text-[13px] text-[#172554]">{m.name}</span>
                <span className="block font-mono text-[10px] text-[#C9CFDC]">{m.key}</span>
              </button>
            )) : (
              <p className="px-3 py-3 text-[12px] text-[#98A1B2]">
                Nothing matches. A KPI has to exist in KPI Master before everyone can be
                measured on it.
              </p>
            )}
          </div>
        </div>
      ) : (
        <Button size="sm" variant="outline" onClick={() => setAdding(true)} disabled={busy}
          data-testid="mandatory-add-open">+ Add a KPI for everyone</Button>
      )}

      <p className="text-[11px] text-[#98A1B2] mt-3 max-w-3xl">
        Removing one takes it off every template and off every employee sheet where it
        carries no mark. A row that already has a recorded mark stays — pulling a scored
        row out of a settled month would silently change that month&rsquo;s percentage.
        Locked sheets are never edited.
      </p>
    </div>
  );
}
